import type { Identifier } from "ra-core";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { Ticket } from "@/modules/types";

type CsatDataProvider = Pick<CrmDataProvider, "sendTicketCsatEmail">;
type CsatResolveDataProvider = Pick<
  CrmDataProvider,
  "sendTicketCsatEmail" | "getOne"
>;

/** Fire-and-forget CSAT after a known resolve. Edge skips when csat_enabled is off. */
export const maybeSendTicketCsatEmail = (
  dataProvider: CsatDataProvider,
  ticketId: Identifier,
) => {
  void dataProvider
    .sendTicketCsatEmail(Number(ticketId))
    .catch(() => undefined);
};

/**
 * After deliver (or other ambiguous paths), send CSAT only if the ticket is
 * resolved *and* was not already resolved before this action (avoids resend on
 * retry). Prefer `confirmedStatus` from the deliver Edge response when available
 * to avoid stale getOne cache. Full Edge `csat_sent_at` idempotency is still open.
 */
export const maybeSendTicketCsatIfResolved = async (
  dataProvider: CsatResolveDataProvider,
  ticketId: Identifier,
  previousStatus?: string | null,
  confirmedStatus?: string | null,
) => {
  if (previousStatus === "resolved") return;
  try {
    if (confirmedStatus != null) {
      if (confirmedStatus !== "resolved") return;
      await dataProvider.sendTicketCsatEmail(Number(ticketId));
      return;
    }
    const { data } = await dataProvider.getOne<Ticket>("tickets", {
      id: ticketId,
    });
    if (data?.status !== "resolved") return;
    await dataProvider.sendTicketCsatEmail(Number(ticketId));
  } catch {
    // Edge / network failures must not block the UI after a successful resolve.
  }
};
